
import React, { useState, useEffect } from 'react';

interface SplashScreenProps {
  onComplete: () => void;
}

export const SplashScreen: React.FC<SplashScreenProps> = ({ onComplete }) => {
  const [phase, setPhase] = useState<'traveling' | 'collision' | 'forming' | 'logo' | 'fadeOut'>('traveling');

  useEffect(() => {
    const timers = [
      setTimeout(() => setPhase('collision'), 1200),
      setTimeout(() => setPhase('forming'), 1600),
      setTimeout(() => setPhase('logo'), 2200),
      setTimeout(() => setPhase('fadeOut'), 3200),
      setTimeout(() => onComplete(), 3800),
    ];

    return () => timers.forEach(clearTimeout);
  }, [onComplete]);

  return (
    <div 
      className={`fixed inset-0 z-[9999] bg-black flex items-center justify-center overflow-hidden transition-opacity duration-500 ${
        phase === 'fadeOut' ? 'opacity-0' : 'opacity-100'
      }`}
    >
      {/* Rosa line from left - traveling animation */}
      <div 
        className={`absolute h-1.5 transition-all ease-out ${
          phase === 'traveling' ? 'animate-pulse' : ''
        }`}
        style={{ 
          top: '50%', 
          transform: 'translateY(-50%)',
          borderRadius: '0 100px 100px 0',
          background: 'linear-gradient(90deg, transparent 0%, #ec4899 30%, #f472b6 70%, #ec4899 100%)',
          boxShadow: '0 0 40px 15px rgba(236,72,153,0.7), 0 0 80px 30px rgba(236,72,153,0.4)',
          left: phase === 'traveling' ? '-10%' : '50%',
          width: phase === 'traveling' ? '60%' : '0%',
          opacity: phase === 'collision' || phase === 'forming' || phase === 'logo' || phase === 'fadeOut' ? 0 : 1,
          transitionDuration: phase === 'traveling' ? '1200ms' : '400ms',
          transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        {/* Glowing head of the line */}
        <div 
          className="absolute right-0 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full"
          style={{
            background: 'radial-gradient(circle, #fff 0%, #f472b6 40%, #ec4899 100%)',
            boxShadow: '0 0 20px 10px rgba(236,72,153,0.9)',
          }}
        />
      </div>

      {/* Celeste line from right - traveling animation */}
      <div 
        className={`absolute h-1.5 transition-all ease-out ${
          phase === 'traveling' ? 'animate-pulse' : ''
        }`}
        style={{ 
          top: '50%', 
          transform: 'translateY(-50%)',
          borderRadius: '100px 0 0 100px',
          background: 'linear-gradient(270deg, transparent 0%, #22d3ee 30%, #67e8f9 70%, #22d3ee 100%)',
          boxShadow: '0 0 40px 15px rgba(34,211,238,0.7), 0 0 80px 30px rgba(34,211,238,0.4)',
          right: phase === 'traveling' ? '-10%' : '50%',
          width: phase === 'traveling' ? '60%' : '0%',
          opacity: phase === 'collision' || phase === 'forming' || phase === 'logo' || phase === 'fadeOut' ? 0 : 1,
          transitionDuration: phase === 'traveling' ? '1200ms' : '400ms',
          transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        {/* Glowing head of the line */}
        <div 
          className="absolute left-0 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full"
          style={{
            background: 'radial-gradient(circle, #fff 0%, #67e8f9 40%, #22d3ee 100%)',
            boxShadow: '0 0 20px 10px rgba(34,211,238,0.9)',
          }}
        />
      </div>

      {/* Collision burst effect */}
      <div 
        className={`absolute transition-all duration-500 ${
          phase === 'collision' || phase === 'forming' ? 'scale-100 opacity-100' : 'scale-0 opacity-0'
        }`}
        style={{
          width: '200px',
          height: '200px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.9) 0%, rgba(236,72,153,0.6) 30%, rgba(34,211,238,0.6) 60%, transparent 80%)',
          boxShadow: '0 0 100px 50px rgba(168,85,247,0.6)',
          borderRadius: '50%',
        }}
      />

      {/* Energy waves during collision */}
      {(phase === 'collision' || phase === 'forming') && (
        <>
          {[...Array(3)].map((_, i) => (
            <div
              key={`wave-${i}`}
              className="absolute rounded-full border-2 animate-ping"
              style={{
                width: `${100 + i * 60}px`,
                height: `${100 + i * 60}px`,
                borderColor: i % 2 === 0 ? 'rgba(236,72,153,0.5)' : 'rgba(34,211,238,0.5)',
                animationDuration: `${0.8 + i * 0.3}s`,
                animationDelay: `${i * 0.15}s`,
              }}
            />
          ))}
        </>
      )}

      {/* Particle effects during collision */}
      {(phase === 'collision' || phase === 'forming') && (
        <>
          {[...Array(16)].map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 rounded-full"
              style={{
                left: '50%',
                top: '50%',
                background: i % 2 === 0 ? '#ec4899' : '#22d3ee',
                boxShadow: `0 0 10px 5px ${i % 2 === 0 ? 'rgba(236,72,153,0.8)' : 'rgba(34,211,238,0.8)'}`,
                transform: `translate(-50%, -50%) rotate(${i * 22.5}deg) translateX(${60 + Math.random() * 80}px)`,
                animation: 'particleExpand 0.8s ease-out forwards',
                animationDelay: `${i * 0.03}s`,
              }}
            />
          ))}
        </>
      )}

      {/* Logo forming from energy */}
      <div 
        className={`relative transition-all ease-out ${
          phase === 'forming' ? 'scale-75 opacity-50 blur-sm' : 
          phase === 'logo' || phase === 'fadeOut' ? 'scale-100 opacity-100 blur-0' : 'scale-0 opacity-0'
        }`}
        style={{
          transitionDuration: phase === 'forming' ? '600ms' : '800ms',
        }}
      >
        {/* Glow effect behind logo */}
        <div 
          className={`absolute inset-0 -z-10 transition-opacity duration-700 ${
            phase === 'logo' || phase === 'fadeOut' ? 'opacity-80' : 'opacity-0'
          }`}
          style={{
            background: 'radial-gradient(ellipse at center, rgba(236,72,153,0.4) 0%, rgba(34,211,238,0.4) 50%, transparent 70%)',
            filter: 'blur(40px)',
            transform: 'scale(1.5)',
          }}
        />
        
        {/* Main logo */}
        <img 
          src="https://static.readdy.ai/image/c11fe3733a927373504823c692bafe5e/026ea768b5e51102379d2820fe22c15a.png" 
          alt="Digital Line Logo"
          className="w-80 h-auto drop-shadow-[0_0_30px_rgba(168,85,247,0.5)]"
        />
        
        {/* Shimmer effect on logo */}
        <div 
          className={`absolute inset-0 transition-opacity duration-500 ${
            phase === 'logo' ? 'opacity-100' : 'opacity-0'
          }`}
          style={{
            background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.3) 50%, transparent 100%)',
            animation: phase === 'logo' ? 'shimmer 1.5s ease-in-out' : 'none',
          }}
        />
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes particleExpand {
          0% {
            transform: translate(-50%, -50%) rotate(var(--rotation)) translateX(0px);
            opacity: 1;
          }
          100% {
            transform: translate(-50%, -50%) rotate(var(--rotation)) translateX(150px);
            opacity: 0;
          }
        }
        
        @keyframes shimmer {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }
      `}</style>
    </div>
  );
};
